/* Reordering the cities with a finger (owner, 2026-10-02: "Let's do the drag and drop for the
   cities for the mobile version pls").

   The phone shell had no dragging at all (2026-09-09), because a vertical drag on a row and a
   vertical scroll of the list are the same gesture on a touch screen and the drag won: the roster
   could not be scrolled past its first screen. The way every phone tells the two apart is time —
   **a finger that rests on a row lifts it; a finger that moves scrolls the list.** So on a touch
   screen the row's own drag listener is off and this arms a timer on `pointerdown`:

   - the finger moves past `slop` before `delay`, or lifts, or the browser takes the gesture for a
     scroll (`pointercancel`): nothing happens, and the list scrolled as it always did;
   - the finger rests for `delay`: `onStart` gets the `pointerdown`, which is what framer-motion's
     `dragControls.start` wants, and from there the drag is the desktop's — the same Reorder, the
     same `utils/dragAutoScroll` at the list's edges.

   Two things the browser does with a resting finger have to be stopped while a row is lifted, and
   `holdTouchScroll` is both: the first `touchmove` after the lift would start a native scroll (and
   a `pointercancel` that ends the drag), so it is cancelled — `touch-action` cannot do it, being
   read once at `touchstart`, before anybody knew this was a drag — and the long-press context menu
   is refused. */

export const LONG_PRESS_MS = 350
export const LONG_PRESS_SLOP_PX = 8

/** Pointer handlers that call `onStart(pointerDownEvent)` once a pointer has rested for `delay`
 *  without moving more than `slop`. Mouse pointers are ignored: the mouse drags at once. */
export const createLongPress = ({ onStart, delay = LONG_PRESS_MS, slop = LONG_PRESS_SLOP_PX }) => {
  let timer = null
  let origin = null

  const cancel = () => {
    if (timer != null) clearTimeout(timer)
    timer = null
    origin = null
  }

  const onPointerDown = (event) => {
    cancel()
    if (event.pointerType === "mouse" || (event.button != null && event.button !== 0)) return
    // React 17+ does not pool events, but the native one is what a drag session reads.
    const pressed = event.nativeEvent || event
    origin = { x: event.clientX, y: event.clientY, id: event.pointerId }
    timer = setTimeout(() => {
      timer = null
      origin = null
      onStart(pressed)
    }, delay)
  }

  const onPointerMove = (event) => {
    if (!origin || event.pointerId !== origin.id) return
    if (Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > slop) cancel()
  }

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: cancel,
    onPointerCancel: cancel,
    cancel,
    isArmed: () => timer != null,
  }
}

/** While a row is lifted: no native scroll and no context menu. Returns the release. */
export const holdTouchScroll = (target = document) => {
  const block = (event) => {
    if (event.cancelable) event.preventDefault()
  }
  target.addEventListener("touchmove", block, { passive: false })
  target.addEventListener("contextmenu", block)
  return () => {
    target.removeEventListener("touchmove", block, { passive: false })
    target.removeEventListener("contextmenu", block)
  }
}
